import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ChevronDown,
  Search,
  FileText,
  FlaskConical,
  Image as ImageIcon,
  Folder,
  CloudUpload,
  Eye,
  Download,
  FileType,
  FileImage,
  Shield,
  Leaf,
  ArrowUpDown,
  SlidersHorizontal,
  Clock,
  LayoutGrid,
  RefreshCw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { buildApiUrl } from "../services/apiHelpers";

/* ============================================================
   DATA (mock)
   ============================================================ */
const documentCategories = [
  "Lab report",
  "Scan / image",
  "Referral letter",
  "Medical certificate",
  "Other document",
];

/* ============================================================
   STYLES
   ============================================================ */
const pageStyles = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&family=Caveat:wght@600;700&display=swap');

.cq-root{min-height:100vh;background:#f2f5fb;font-family:'DM Sans',sans-serif;color:#1e2a44;-webkit-font-smoothing:antialiased;}
.cq-root *{box-sizing:border-box;}

/* ---------- Top nav ---------- */
.cq-nav{display:flex;align-items:center;gap:20px;padding:14px clamp(16px,3vw,40px);background:#fff;border-bottom:1px solid #e8ecf5;position:sticky;top:0;z-index:50;}
.cq-brand{display:flex;align-items:center;gap:11px;min-width:210px;}
.cq-logo{width:40px;height:40px;border-radius:11px;display:grid;place-items:center;background:#f3f0ff;}
.cq-brand-text strong{display:block;font:700 20px 'Space Grotesk',sans-serif;color:#1a2340;letter-spacing:-.01em;}
.cq-brand-text small{display:block;font-size:11px;color:#9aa6bd;margin-top:1px;}

.cq-search{flex:1;max-width:640px;margin:0 auto;display:flex;align-items:center;gap:10px;padding:0 16px;height:44px;background:#f4f6fb;border:1px solid #e8ecf5;border-radius:12px;color:#9aa6bd;}
.cq-search input{flex:1;border:0;background:transparent;outline:none;font-size:14px;color:#1e2a44;}
.cq-search input::placeholder{color:#9aa6bd;}
.cq-kbd{font-size:12px;color:#aab4c8;background:#eaeef6;border:1px solid #e0e6f0;border-radius:6px;padding:2px 7px;font-weight:600;}

.cq-nav-right{display:flex;align-items:center;gap:8px;min-width:210px;justify-content:flex-end;}
.cq-icon-btn{position:relative;width:42px;height:42px;border:0;border-radius:12px;background:#f4f6fb;color:#64748b;display:grid;place-items:center;cursor:pointer;transition:background .15s ease,color .15s ease;}
.cq-icon-btn:hover{background:#e9edf6;color:#334155;}
.cq-dot{position:absolute;top:9px;right:10px;width:8px;height:8px;border-radius:50%;background:#ef4444;border:2px solid #fff;}
.cq-user{display:flex;align-items:center;gap:10px;padding:5px 10px 5px 5px;border:0;background:transparent;cursor:pointer;border-radius:12px;transition:background .15s ease;}
.cq-user:hover{background:#f4f6fb;}
.cq-user-avatar{width:40px;height:40px;border-radius:50%;background:#e6e2fb;color:#6d47d9;display:grid;place-items:center;font:700 14px 'Space Grotesk',sans-serif;}
.cq-user-info{text-align:left;}
.cq-user-info strong{display:block;font-size:14px;font-weight:700;color:#1a2340;}
.cq-user-info small{display:block;font-size:12px;color:#9aa6bd;}
.cq-user svg{color:#aab4c8;}

/* ---------- Main ---------- */
.cq-main{width:100%;max-width:1500px;margin:0 auto;padding:28px clamp(18px,3vw,40px) 34px;display:flex;flex-direction:column;gap:20px;}

/* ---------- Patient bar ---------- */
.cq-patient-bar{display:flex;align-items:center;gap:18px;flex-wrap:wrap;background:#fff;border:1px solid #e8ecf5;border-radius:18px;padding:18px 22px;box-shadow:0 10px 26px rgba(30,42,80,.04);}
.cq-patient-left{display:flex;align-items:center;gap:16px;min-width:260px;}
.cq-patient-avatar{width:60px;height:60px;border-radius:50%;background:#ece7fb;color:#6d47d9;display:grid;place-items:center;font:700 18px 'Space Grotesk',sans-serif;}
.cq-patient-name{display:flex;align-items:center;gap:9px;font:700 23px 'Space Grotesk',sans-serif;color:#141d38;letter-spacing:-.01em;}
.cq-gender{font-size:16px;color:#3b6cf6;}
.cq-patient-meta{display:flex;align-items:center;gap:8px;margin-top:4px;font-size:13.5px;color:#6b7789;font-weight:500;}
.cq-sep{color:#c6cedd;}
.cq-uhid{background:#eef0ff;color:#5b62d6;font-weight:700;padding:2px 9px;border-radius:7px;font-size:12.5px;}
.cq-patient-doc{margin-top:5px;font-size:13px;color:#6b7789;font-weight:600;}
.cq-bullet{color:#c6cedd;}

.cq-patient-actions{display:flex;align-items:center;gap:9px;margin-left:auto;}
.cq-pill-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 16px;border:1px solid #e2e8f3;border-radius:11px;background:#fff;color:#3d4c66;font-size:13.5px;font-weight:600;cursor:pointer;transition:all .15s ease;}
.cq-pill-btn:hover{background:#f6f8fd;border-color:#cfd8ea;color:#1e2a44;}
.cq-pill-btn svg{color:#7b8aa3;}
.cq-square-btn{padding:10px 11px;}

.cq-lastvisit{display:flex;align-items:center;gap:12px;padding:12px 16px;border-radius:14px;background:linear-gradient(135deg,#eef2ff,#f4f0ff);border:1px solid #e6e9fb;}
.cq-lastvisit-icon{width:42px;height:42px;border-radius:11px;background:#fff;color:#5b62d6;display:grid;place-items:center;box-shadow:0 4px 10px rgba(91,98,214,.12);}
.cq-lastvisit small{display:block;font-size:11px;color:#8a94ad;font-weight:600;}
.cq-lastvisit strong{display:block;font:700 14px 'Space Grotesk',sans-serif;color:#1a2340;margin-top:1px;}
.cq-lastvisit span{font-size:12px;color:#8a94ad;}

/* ---------- Hero ---------- */
.cq-hero{position:relative;overflow:hidden;display:flex;justify-content:space-between;align-items:center;gap:20px;flex-wrap:wrap;padding:26px 30px;border-radius:20px;background:linear-gradient(120deg,#dbe6fb 0%,#e7edfc 45%,#eef2fe 100%);border:1px solid #d8e2f7;}
.cq-hero:before{content:'';position:absolute;inset:auto -40px -120px auto;width:420px;height:220px;background:radial-gradient(circle at 60% 30%,rgba(255,255,255,.55),rgba(255,255,255,0) 65%);}
.cq-hero>*{position:relative;z-index:1;}
.cq-hero-title{display:flex;align-items:center;gap:18px;}
.cq-hero-icon{width:56px;height:56px;border-radius:15px;background:#fff;color:#3b6cf6;display:grid;place-items:center;box-shadow:0 8px 20px rgba(59,108,246,.14);}
.cq-hero-title h1{margin:0;font:700 27px 'Space Grotesk',sans-serif;color:#101a33;letter-spacing:-.02em;}
.cq-hero-title p{margin:5px 0 0;color:#5a6b8a;font-size:14px;}
.cq-hero-quote{font-family:'Caveat',cursive;font-size:26px;color:#5b8bd6;line-height:1.15;text-align:right;font-weight:700;}
.cq-heart{color:#e07a9c;}

/* ---------- Summary ---------- */
.cq-summary{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;}
.cq-summary-card{display:flex;align-items:center;gap:15px;padding:18px 20px;background:#fff;border:1px solid #e8ecf5;border-radius:16px;box-shadow:0 8px 22px rgba(30,42,80,.04);transition:transform .15s ease,box-shadow .15s ease;}
.cq-summary-card:nth-child(2){background:#faf8ff;border-color:#efe9fb;}
.cq-summary-card:hover{transform:translateY(-2px);box-shadow:0 14px 28px rgba(30,42,80,.09);}
.cq-summary-icon{width:52px;height:52px;border-radius:14px;display:grid;place-items:center;flex-shrink:0;}
.cq-summary-body strong{display:block;font:700 26px 'Space Grotesk',sans-serif;color:#101a33;line-height:1.05;}
.cq-summary-body span{display:block;font-size:14px;font-weight:700;margin-top:2px;}
.cq-summary-body small{display:block;font-size:12px;color:#98a2b6;margin-top:2px;}

/* ---------- Content ---------- */
.cq-content{display:grid;grid-template-columns:minmax(0,1.7fr) minmax(320px,.8fr);gap:20px;align-items:stretch;}
.cq-panel{background:#fff;border:1px solid #e8ecf5;border-radius:18px;box-shadow:0 10px 26px rgba(30,42,80,.05);}
.cq-docs-panel{overflow:hidden;display:flex;flex-direction:column;min-width:0;}

.cq-docs-toolbar{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;padding:8px 22px 0;border-bottom:1px solid #eef1f8;}
.cq-tabs{display:flex;gap:22px;}
.cq-tab{position:relative;display:inline-flex;align-items:center;gap:7px;border:0;background:transparent;padding:16px 2px;font-size:14px;font-weight:600;color:#8794aa;cursor:pointer;transition:color .15s ease;}
.cq-tab.is-active{color:#3b6cf6;}
.cq-tab.is-active:after{content:'';position:absolute;left:0;right:0;bottom:0;height:3px;border-radius:3px 3px 0 0;background:#3b6cf6;}
.cq-tab:hover{color:#3b6cf6;}

.cq-docs-filters{display:flex;align-items:center;gap:10px;padding-bottom:10px;flex:1 1 420px;justify-content:flex-end;}
.cq-doc-search{display:flex;align-items:center;gap:8px;height:42px;padding:0 13px;background:#f4f6fb;border:1px solid #e8ecf5;border-radius:11px;color:#9aa6bd;min-width:220px;}
.cq-doc-search input{border:0;background:transparent;outline:none;font-size:13.5px;color:#1e2a44;width:100%;}
.cq-doc-search input::placeholder{color:#9aa6bd;}
.cq-select-wrap{position:relative;display:flex;align-items:center;gap:8px;height:42px;padding:0 34px 0 13px;background:#fff;border:1px solid #e2e8f3;border-radius:11px;color:#64748b;}
.cq-select-wrap select{border:0;background:transparent;outline:none;font-size:13.5px;font-weight:600;color:#3d4c66;cursor:pointer;-webkit-appearance:none;appearance:none;padding-right:4px;}
.cq-select-caret{position:absolute;right:11px;color:#9aa6bd;pointer-events:none;}
.cq-sort-btn{width:42px;height:42px;border:1px solid #e2e8f3;border-radius:11px;background:#fff;color:#64748b;display:grid;place-items:center;cursor:pointer;transition:all .15s ease;}
.cq-sort-btn:hover{background:#f6f8fd;color:#334155;}

/* ---------- Table ---------- */
.cq-table{padding:6px 0 8px;flex:1;min-width:0;}
.cq-thead,.cq-row{display:grid;grid-template-columns:38px minmax(190px,1fr) 145px 82px 150px 96px;align-items:center;gap:12px;padding:12px 22px;}
.cq-thead{color:#94a0b4;font-size:11.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;border-bottom:1px solid #eef1f8;}
.cq-row{border-bottom:1px solid #f1f4fa;transition:background .12s ease;}
.cq-row:last-child{border-bottom:0;}
.cq-row:hover{background:#f9fbfe;}
.cq-index{color:#9aa6bd;font-size:14px;font-weight:600;}
.cq-doc-name{display:flex;align-items:center;gap:12px;min-width:0;}
.cq-file-icon{width:42px;height:42px;border-radius:11px;display:grid;place-items:center;flex-shrink:0;}
.cq-doc-name-text{min-width:0;}
.cq-doc-name-text strong{display:block;font-size:14px;font-weight:700;color:#1a2340;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.cq-doc-name-text small{display:block;font-size:12px;color:#98a2b6;margin-top:2px;}
.cq-cat-chip{display:inline-flex;align-items:center;gap:6px;padding:6px 11px;border-radius:8px;background:#eef0ff;color:#5b62d6;font-size:12.5px;font-weight:700;}
.cq-cat-chip svg{color:#7c3aed;}
.cq-cell-muted{color:#6b7789;font-size:13.5px;font-weight:600;}
.cq-added strong{display:block;font-size:13px;font-weight:700;color:#3d4c66;}
.cq-added small{display:block;font-size:12px;color:#98a2b6;margin-top:1px;}
.cq-row-actions{display:flex;align-items:center;gap:4px;}
.cq-row-actions button{width:34px;height:34px;border:0;border-radius:9px;background:transparent;color:#3b6cf6;display:grid;place-items:center;cursor:pointer;transition:background .12s ease;}
.cq-row-actions button:hover{background:#eef2ff;}
.cq-row-actions button:last-child{color:#8794aa;}
.cq-empty{display:grid;place-items:center;align-content:center;gap:8px;min-height:224px;padding:30px 20px;color:#98a2b6;font-size:14px;text-align:center;}
.cq-empty svg{color:#c6cedd;}

/* ---------- Side ---------- */
.cq-side{display:flex;flex-direction:column;gap:20px;min-width:0;}
.cq-upload-panel{padding:22px;min-height:100%;}
.cq-upload-head{display:flex;align-items:flex-start;gap:12px;margin-bottom:16px;}
.cq-upload-icon{width:40px;height:40px;border-radius:11px;background:#e8eeff;color:#3b6cf6;display:grid;place-items:center;flex-shrink:0;}
.cq-upload-head h3{margin:0;font:700 17px 'Space Grotesk',sans-serif;color:#101a33;}
.cq-upload-head p{margin:4px 0 0;font-size:12.5px;color:#8794aa;line-height:1.4;}
.cq-dropzone{display:flex;flex-direction:column;align-items:center;gap:8px;text-align:center;padding:26px 20px;border:1.6px dashed #b9c9f5;border-radius:15px;background:linear-gradient(160deg,#f6f9ff,#eef3ff);transition:all .16s ease;}
.cq-dropzone.is-dragging{border-color:#6d8cf0;background:#e9eeff;}
.cq-drop-circle{width:60px;height:60px;border-radius:50%;background:#e3ebff;color:#3b6cf6;display:grid;place-items:center;margin-bottom:2px;}
.cq-dropzone strong{font:700 15px 'Space Grotesk',sans-serif;color:#2f66e6;}
.cq-or{font-size:12.5px;color:#9aa6bd;}
.cq-choose-btn{border:0;border-radius:11px;padding:11px 26px;background:#3b6cf6;color:#fff;font:700 13.5px 'DM Sans',sans-serif;cursor:pointer;box-shadow:0 8px 18px rgba(59,108,246,.28);transition:background .15s ease;}
.cq-choose-btn:hover{background:#2f5ce0;}
.cq-dropzone small{font-size:11.5px;color:#8794aa;line-height:1.6;margin-top:4px;}

.cq-quick-panel{padding:20px;}
.cq-quick-panel h3{margin:0 0 14px;font:700 17px 'Space Grotesk',sans-serif;color:#101a33;}
.cq-quick-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.cq-quick-btn{display:flex;align-items:center;gap:11px;padding:14px;border:1px solid #eef1f8;border-radius:13px;background:#fbfcfe;color:#3d4c66;font-size:13.5px;font-weight:600;cursor:pointer;transition:all .15s ease;}
.cq-quick-btn:hover{background:#f4f7fd;border-color:#dbe3f2;transform:translateY(-1px);}
.cq-quick-icon{width:38px;height:38px;border-radius:10px;display:grid;place-items:center;flex-shrink:0;}

/* ---------- Footer ---------- */
.cq-footer{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;padding:6px 4px;font-size:13px;color:#8794aa;}
.cq-footer-left,.cq-footer-right{display:flex;align-items:center;gap:9px;}
.cq-footer-left svg,.cq-footer-right svg{color:#22c07f;}

/* ---------- Upload modal ---------- */
.rud-overlay{position:fixed;inset:0;z-index:3000;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(2px);}
.rud-card{width:min(100%,480px);background:#fff;border-radius:18px;box-shadow:0 24px 70px rgba(15,23,42,.35);overflow:hidden;font-family:'DM Sans',sans-serif;animation:rud-in .18s ease;}
@keyframes rud-in{from{opacity:0;transform:translateY(10px) scale(.98);}to{opacity:1;transform:none;}}
.rud-head{padding:20px 22px;background:linear-gradient(120deg,#2f5ce0,#3b6cf6);color:#fff;}
.rud-step{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;opacity:.8;}
.rud-head h3{margin:6px 0 0;font:700 19px 'Space Grotesk',sans-serif;}
.rud-body{padding:22px;}
.rud-file-chip{display:flex;align-items:center;gap:10px;padding:11px;margin-bottom:18px;background:#f4f6fb;border-radius:11px;color:#48586a;font-size:12.5px;font-weight:600;overflow:hidden;}
.rud-file-chip span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.rud-label{display:block;font-size:12.5px;font-weight:700;color:#334155;margin-bottom:6px;}
.rud-label em{color:#ef4444;font-style:normal;}
.rud-input{width:100%;padding:11px 12px;border:1px solid #dde3f0;border-radius:10px;font-size:13.5px;color:#0f172a;margin-bottom:16px;background:#fff;font-family:'DM Sans',sans-serif;}
.rud-input:focus{outline:2px solid #93b4fb;outline-offset:1px;border-color:#93b4fb;}
.rud-actions{display:flex;justify-content:flex-end;gap:9px;}
.rud-btn-ghost{padding:10px 16px;border:1px solid #dde3f0;border-radius:10px;background:#fff;color:#64748b;font-weight:700;cursor:pointer;}
.rud-btn-ghost:hover{background:#f6f8fd;}
.rud-btn-solid{display:inline-flex;align-items:center;gap:7px;padding:10px 18px;border:0;border-radius:10px;background:#3b6cf6;color:#fff;font-weight:700;cursor:pointer;}
.rud-btn-solid:hover:not(:disabled){background:#2f5ce0;}
.rud-btn-solid:disabled{background:#c7d2e6;cursor:not-allowed;}
.anim-spin{animation:cq-spin .8s linear infinite;}
@keyframes cq-spin{to{transform:rotate(360deg);}}

/* ---------- Document action modal ---------- */
.dam-overlay{position:fixed;inset:0;z-index:2900;background:rgba(15,23,42,.48);display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(2px);}
.dam-card{width:min(100%,620px);max-height:min(86vh,680px);display:flex;flex-direction:column;background:#fff;border:1px solid #e3e9f4;border-radius:18px;box-shadow:0 24px 70px rgba(15,23,42,.3);overflow:hidden;animation:rud-in .18s ease;}
.dam-card.action-loading{width:min(92vw,420px);max-height:none;}
.dam-head{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:17px 20px;border-bottom:1px solid #eef1f8;}
.dam-title{display:flex;align-items:center;gap:10px;min-width:0;}
.dam-title-icon{width:36px;height:36px;border-radius:10px;background:#e8eeff;color:#3b6cf6;display:grid;place-items:center;flex-shrink:0;}
.dam-title strong{display:block;color:#17213d;font:700 15px 'Space Grotesk',sans-serif;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.dam-title small{display:block;color:#8b98ae;font-size:11.5px;margin-top:3px;}
.dam-close{width:32px;height:32px;border:0;border-radius:9px;background:#f4f6fb;color:#718096;display:grid;place-items:center;cursor:pointer;flex-shrink:0;}
.dam-close:hover{background:#e9edf6;color:#334155;}
.dam-body{padding:20px;overflow:auto;}
.dam-progress-track{height:8px;background:#e8edf7;border-radius:99px;overflow:hidden;}
.dam-progress-bar{height:100%;background:linear-gradient(90deg,#3b6cf6,#6d47d9);border-radius:inherit;transition:width .2s ease;}
.dam-status{display:flex;justify-content:space-between;gap:12px;margin-top:9px;color:#718096;font-size:12px;}
.dam-status strong{color:#3b6cf6;}
.dam-message{text-align:center;padding:28px 10px 8px;color:#718096;font-size:13px;}
.dam-message strong{display:block;color:#243252;font-size:15px;margin-bottom:5px;}

/* ---------- Responsive ---------- */
@media(max-width:1180px){
  .cq-content{grid-template-columns:1fr;}
  .cq-side{display:grid;grid-template-columns:minmax(0,1fr);}
  .cq-summary{grid-template-columns:repeat(2,1fr);}
}
@media(max-width:820px){
  .cq-search{display:none;}
  .cq-patient-actions{margin-left:0;width:100%;flex-wrap:wrap;}
  .cq-lastvisit{margin-left:auto;}
  .cq-thead{display:none;}
  .cq-row{grid-template-columns:30px minmax(0,1fr) auto;gap:10px;padding:12px 16px;}
  .cq-thead{padding-left:16px;padding-right:16px;}
  .cq-docs-toolbar{align-items:stretch;padding-left:16px;padding-right:16px;}
  .cq-docs-filters{flex-basis:100%;justify-content:stretch;}
  .cq-doc-search{flex:1;min-width:0;}
  .cq-row>span:nth-child(3),.cq-row>.cq-cell-muted,.cq-added{display:none;}
  .cq-hero-quote{display:none;}
}
@media(max-width:560px){
  .cq-summary{grid-template-columns:1fr;}
  .cq-brand-text small{display:none;}
  .cq-user-info{display:none;}
}
`;

/* ============================================================
   UPLOAD MODAL
   ============================================================ */
function UploadModal({
  selectedFile,
  reportName,
  setReportName,
  documentType,
  setDocumentType,
  isUploading,
  onUpload,
  onClose,
}) {
  const canUpload = Boolean(selectedFile && reportName.trim() && documentType && !isUploading);

  return (
    <div
      className="rud-overlay"
      data-testid="upload-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && !isUploading && onClose()}
    >
      <div role="dialog" aria-modal="true" className="rud-card" data-testid="upload-modal">
        <div className="rud-head">
          <div className="rud-step">Step 2 of 2</div>
          <h3>Name and upload report</h3>
        </div>
        <div className="rud-body">
          <div className="rud-file-chip" data-testid="upload-modal-file-chip">
            <FileText size={16} />
            <span>{selectedFile?.name}</span>
          </div>

          <label className="rud-label" htmlFor="report-name">
            Report file name <em>*</em>
          </label>
          <input
            id="report-name"
            data-testid="upload-modal-name-input"
            className="rud-input"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            placeholder="e.g. blood-test-september"
            autoFocus
          />

          <label className="rud-label" htmlFor="doc-type">
            Document type <em>*</em>
          </label>
          <select
            id="doc-type"
            data-testid="upload-modal-type-select"
            className="rud-input"
            required
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
          >
            <option value="">Select document type</option>
            {documentCategories.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          <div className="rud-actions">
            <button
              type="button"
              data-testid="upload-modal-cancel-btn"
              className="rud-btn-ghost"
              disabled={isUploading}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="upload-modal-submit-btn"
              className="rud-btn-solid"
              disabled={!canUpload}
              onClick={onUpload}
            >
              {isUploading && <RefreshCw size={14} className="anim-spin" />}
              {isUploading ? "Uploading..." : "Upload report"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { UploadModal as ReportUploadModal };

function DocumentActionModal({ documentRecord, mode, progress, status, error, onClose }) {
  const isPreview = mode === "view";

  return (
    <div className="dam-overlay" data-testid="document-action-modal-overlay">
      <div className="dam-card action-loading" role="dialog" aria-modal="true" data-testid="document-action-modal">
        <div className="dam-head">
          <div className="dam-title">
            <span className="dam-title-icon">{isPreview ? <Eye size={17} /> : <Download size={17} />}</span>
            <div>
              <strong>{documentRecord.fileName}</strong>
              <small>{isPreview ? "Preparing preview" : "Preparing download"}</small>
            </div>
          </div>
          {status !== "loading" && (
            <button type="button" className="dam-close" title="Close" onClick={onClose}>
              <X size={17} />
            </button>
          )}
        </div>
        <div className="dam-body">
          <div className="dam-progress-track" aria-label="Document loading progress">
            <div className="dam-progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <div className="dam-status">
            <span>{error || (status === "loading" ? "Loading document..." : isPreview ? "Preview ready" : "Download ready")}</span>
            <strong>{progress}%</strong>
          </div>

          {status === "error" && <div className="dam-message"><strong>Could not load document</strong>Please close this window and try again.</div>}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PAGE
   ============================================================ */
const fileVisual = (document) =>
  document.contentType === "application/pdf" || document.fileName?.toLowerCase().endsWith(".pdf")
    ? { icon: <FileType size={18} />, bg: "#fde5e5", fg: "#e0483c" }
    : { icon: <FileImage size={18} />, bg: "#d5f3ea", fg: "#0d9488" };

const formatBackendDateTime = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : {
      timestamp: fallback.getTime(),
      date: fallback.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
      time: fallback.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }),
    };
  }

  const [, year, month, day, hour, minute, second = "0"] = match;
  const localDate = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
  return {
    timestamp: localDate.getTime(),
    date: `${day} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"][Number(month) - 1]} ${year}`,
    time: `${hour}:${minute}`,
  };
};

export default function ReportsDocumentsPage({ patient, clinicId }) {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("this");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [dragActive, setDragActive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [reportName, setReportName] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [documentAction, setDocumentAction] = useState(null);
  const [actionState, setActionState] = useState({ progress: 8, status: "loading", objectUrl: null, error: "" });
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!patient?.patientId || !clinicId) {
      setDocuments([]);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    axios
      .get(buildApiUrl(`clinical-documents/patient/${patient.patientId}`), {
        headers: { "X-Clinic-Id": clinicId },
        withCredentials: true,
      })
      .then((response) => {
        if (!cancelled) setDocuments(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        if (!cancelled) {
          setDocuments([]);
          toast.error(error.response?.data?.message || "Unable to load patient documents");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clinicId, patient?.patientId]);

  const thisVisit = useMemo(
    () => documents.filter((document) => document.appointmentId === patient?.id),
    [documents, patient?.id]
  );
  const prevVisit = useMemo(
    () => documents.filter((document) => document.appointmentId !== patient?.id),
    [documents, patient?.id]
  );

  const visible = useMemo(() => {
    const base = activeTab === "this" ? thisVisit : prevVisit;
    return base
      .filter((d) => {
        const matchQuery = (d.fileName || "").toLowerCase().includes(query.toLowerCase());
        const matchCat = !category || d.documentType === category;
        return matchQuery && matchCat;
      })
      .sort((left, right) => {
        const leftTime = formatBackendDateTime(left.createdAt)?.timestamp || 0;
        const rightTime = formatBackendDateTime(right.createdAt)?.timestamp || 0;
        return sortOrder === "newest" ? rightTime - leftTime : leftTime - rightTime;
      });
  }, [activeTab, query, category, sortOrder, thisVisit, prevVisit]);

  const counts = {
    all: thisVisit.length,
    lab: thisVisit.filter((d) => d.documentType === "Lab report").length,
    scan: thisVisit.filter((d) => d.documentType === "Scan / image").length,
    other: thisVisit.filter((d) => !["Lab report", "Scan / image"].includes(d.documentType)).length,
  };

  const chooseFile = (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Document exceeds the 50MB limit");
      return;
    }
    setSelectedFile(file);
    setReportName(file.name.replace(/\.[^.]+$/, ""));
    setDocumentType("");
    setShowModal(true);
  };

  const handleFiles = (e) => {
    chooseFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    chooseFile(e.dataTransfer.files?.[0]);
  };

  const uploadReport = async () => {
    if (!selectedFile || !reportName.trim() || !documentType) {
      toast.error("Enter a filename and select a document type");
      return;
    }
    if (!patient?.id || !patient?.patientId || !clinicId) {
      toast.error("Patient context is unavailable");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("appointmentId", patient.id);
      formData.append("patientId", patient.patientId);
      formData.append("documentType", documentType);
      formData.append("reportName", reportName.trim());
      const response = await axios.post(buildApiUrl("clinical-documents/upload"), formData, {
        headers: { "X-Clinic-Id": clinicId },
        withCredentials: true,
      });
      setDocuments((previous) => [response.data, ...previous]);
      setShowModal(false);
      setSelectedFile(null);
      setReportName("");
      setDocumentType("");
      setActiveTab("this");
      toast.success("Report uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Document upload failed");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!documentAction) return undefined;
    let cancelled = false;
    let progress = 8;
    let progressTimer;
    setActionState({ progress, status: "loading", objectUrl: null, error: "" });

    progressTimer = window.setInterval(() => {
      progress = Math.min(progress + 7, 88);
      if (!cancelled) setActionState((current) => ({ ...current, progress }));
    }, 180);

    fetch(buildApiUrl(`clinical-documents/${documentAction.document.id}/content`), {
      headers: { "X-Clinic-Id": clinicId },
      credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(documentAction.mode === "view" ? "Unable to open document" : "Unable to download document");
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        progress = 100;
        if (documentAction.mode === "download") {
          const link = document.createElement("a");
          link.href = objectUrl;
          link.download = documentAction.document.fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
          setDocumentAction(null);
        } else {
          window.open(objectUrl, "_blank", "noopener,noreferrer");
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
          setDocumentAction(null);
        }
      })
      .catch((error) => {
        if (!cancelled) setActionState({ progress: 100, status: "error", objectUrl: null, error: error.message });
      })
      .finally(() => window.clearInterval(progressTimer));

    return () => {
      cancelled = true;
      window.clearInterval(progressTimer);
      setActionState((current) => {
        if (current.objectUrl) URL.revokeObjectURL(current.objectUrl);
        return current;
      });
    };
  }, [clinicId, documentAction]);

  const openDocument = (documentRecord) => {
    setDocumentAction({ document: documentRecord, mode: "view" });
  };

  const downloadDocument = (documentRecord) => {
    setDocumentAction({ document: documentRecord, mode: "download" });
  };

  const closeDocumentAction = () => {
    setDocumentAction(null);
  };

  const summaryCards = [
    { value: counts.all, label: "All documents", sub: "Total files in this visit", icon: <FileText size={22} />, bg: "#e8eeff", fg: "#3b6cf6" },
    { value: counts.lab, label: "Lab reports", sub: "Lab results and investigations", icon: <FlaskConical size={22} />, bg: "#f1ecff", fg: "#7c3aed" },
    { value: counts.scan, label: "Scans & images", sub: "X-rays, MRI, scans, photos", icon: <ImageIcon size={22} />, bg: "#d9f6ec", fg: "#0d9488" },
    { value: counts.other, label: "Other documents", sub: "Referrals, notes", icon: <Folder size={22} />, bg: "#fdeecf", fg: "#e08b1a" },
  ];

  return (
    <div className="cq-root" data-testid="reports-documents-page">
      <style>{pageStyles}</style>

      <main className="cq-main">
        {/* ---------- Hero ---------- */}
        <section className="cq-hero">
          <div className="cq-hero-title">
            <span className="cq-hero-icon">
              <FileText size={28} />
            </span>
            <div>
              <h1>Reports &amp; Documents</h1>
              <p>View, upload, and manage all patient documents in one place.</p>
            </div>
          </div>
          <div className="cq-hero-quote">
            Better records
            <br />
            Better care <span className="cq-heart">♡</span>
          </div>
        </section>

        {/* ---------- Summary cards ---------- */}
        <section className="cq-summary" data-testid="summary-cards">
          {summaryCards.map((c) => (
            <div className="cq-summary-card" key={c.label}>
              <span className="cq-summary-icon" style={{ background: c.bg, color: c.fg }}>
                {c.icon}
              </span>
              <div className="cq-summary-body">
                <strong>{c.value}</strong>
                <span style={{ color: c.fg }}>{c.label}</span>
                <small>{c.sub}</small>
              </div>
            </div>
          ))}
        </section>

        {/* ---------- Content ---------- */}
        <section className="cq-content">
          <div className="cq-panel cq-docs-panel">
            <div className="cq-docs-toolbar">
              <div className="cq-tabs">
                <button
                  data-testid="tab-this-visit"
                  className={`cq-tab ${activeTab === "this" ? "is-active" : ""}`}
                  onClick={() => setActiveTab("this")}
                >
                  <LayoutGrid size={15} /> This Visit ({thisVisit.length})
                </button>
                <button
                  data-testid="tab-previous-visits"
                  className={`cq-tab ${activeTab === "previous" ? "is-active" : ""}`}
                  onClick={() => setActiveTab("previous")}
                >
                  <Clock size={15} /> Previous Visits ({prevVisit.length})
                </button>
              </div>

              <div className="cq-docs-filters">
                <div className="cq-doc-search">
                  <Search size={16} />
                  <input
                    data-testid="document-search-input"
                    placeholder="Search documents..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <div className="cq-select-wrap">
                  <SlidersHorizontal size={15} />
                  <select
                    data-testid="category-filter-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">All categories</option>
                    {documentCategories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={15} className="cq-select-caret" />
                </div>
                <button
                  className="cq-sort-btn"
                  title={sortOrder === "newest" ? "Showing newest first" : "Showing oldest first"}
                  data-testid="sort-btn"
                  aria-label="Toggle document sort order"
                  onClick={() => setSortOrder((current) => (current === "newest" ? "oldest" : "newest"))}
                >
                  <ArrowUpDown size={16} />
                </button>
              </div>
            </div>

            <div className="cq-table" data-testid="documents-table">
              <div className="cq-thead">
                <span>#</span>
                <span>Document</span>
                <span>Category</span>
                <span>Size</span>
                <span>Added on</span>
                <span>Actions</span>
              </div>

              {isLoading ? (
                <div className="cq-empty" data-testid="documents-loading">
                  <RefreshCw size={26} className="anim-spin" />
                  <strong>Loading documents...</strong>
                </div>
              ) : visible.length === 0 ? (
                <div className="cq-empty" data-testid="documents-empty">
                  <FileText size={26} />
                  <strong>No documents found</strong>
                </div>
              ) : (
                visible.map((doc, index) => {
                  const v = fileVisual(doc);
                  const addedAt = formatBackendDateTime(doc.createdAt);
                  return (
                    <div className="cq-row" key={doc.id} data-testid={`document-row-${doc.id}`}>
                      <span className="cq-index">{index + 1}</span>
                      <div className="cq-doc-name">
                        <span className="cq-file-icon" style={{ background: v.bg, color: v.fg }}>
                          {v.icon}
                        </span>
                        <span className="cq-doc-name-text">
                          <strong>{doc.fileName}</strong>
                          <small>{doc.appointmentId === patient?.id ? "Patient visit document" : "Previous visit document"}</small>
                        </span>
                      </div>
                      <span>
                        <span className="cq-cat-chip">
                          <FlaskConical size={13} /> {doc.documentType}
                        </span>
                      </span>
                      <span className="cq-cell-muted">
                        {doc.fileSize ? `${(doc.fileSize / 1024 / 1024).toFixed(1)} MB` : "-"}
                      </span>
                      <span className="cq-added">
                        <strong>{addedAt?.date || "-"}</strong>
                        <small>{addedAt?.time || ""}</small>
                      </span>
                      <div className="cq-row-actions">
                        <button title="View" data-testid={`view-btn-${doc.id}`} onClick={() => openDocument(doc)}>
                          <Eye size={17} />
                        </button>
                        <button title="Download" data-testid={`download-btn-${doc.id}`} onClick={() => downloadDocument(doc)}>
                          <Download size={17} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="cq-side">
            <div className="cq-panel cq-upload-panel">
              <div className="cq-upload-head">
                <span className="cq-upload-icon">
                  <CloudUpload size={19} />
                </span>
                <div>
                  <h3>Add a document</h3>
                  <p>Upload reports, scans, referrals, and other patient files.</p>
                </div>
              </div>

              <div
                data-testid="upload-dropzone"
                className={`cq-dropzone ${dragActive ? "is-dragging" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
              >
                <span className="cq-drop-circle">
                  <CloudUpload size={30} strokeWidth={1.8} />
                </span>
                <strong>Drag &amp; drop files here</strong>
                <span className="cq-or">or</span>
                <button className="cq-choose-btn" data-testid="choose-files-btn" onClick={() => fileInputRef.current?.click()}>
                  Choose files
                </button>
                <small>
                  Supported formats: PDF, JPG, PNG, DICOM
                  <br />
                  Max file size: 50 MB
                </small>
              </div>
              <input
                ref={fileInputRef}
                data-testid="file-input"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={handleFiles}
                style={{ display: "none" }}
              />
            </div>

          </div>
        </section>

        {/* ---------- Footer ---------- */}
        <footer className="cq-footer">
          <div className="cq-footer-left">
            <Shield size={17} />
            All documents are securely stored and accessible only to authorized staff.
          </div>
          <div className="cq-footer-right">
            <Leaf size={17} />
            Digital records. A healthier tomorrow.
          </div>
        </footer>
      </main>

      {showModal && (
        <UploadModal
          selectedFile={selectedFile}
          reportName={reportName}
          setReportName={setReportName}
          documentType={documentType}
          setDocumentType={setDocumentType}
          isUploading={uploading}
          onUpload={uploadReport}
          onClose={() => {
            setShowModal(false);
            setSelectedFile(null);
          }}
        />
      )}
      {documentAction && (
        <DocumentActionModal
          documentRecord={documentAction.document}
          mode={documentAction.mode}
          progress={actionState.progress}
          status={actionState.status}
          error={actionState.error}
          onClose={closeDocumentAction}
        />
      )}
    </div>
  );
}
